import React, { Fragment } from "react";
import classNames from "classnames";
import withStyles from "elevate-ui/withStyles";
import startCase from "lodash/startCase";
import LazyLoad from "react-lazy-load";
import Disclaimer from "./Disclaimer";

type Props = {
  classes: Object,
  className: string,
  post: Object,
};

const BlogDisclaimer = ({ listing }) => (
  <Disclaimer
    compliance={listing.compliance}
    listing={listing}
    complianceKey="blogListListingDisclaimer"
  />
);

const renderBlogDisclaimer = (listing) => {
  if (listing.compliance && listing.compliance.blogListListingDisclaimer) {
    return <BlogDisclaimer listing={listing} />;
  }
  return;
};

const BlogPostCard = ({ classes, className, post }: Props) => {
  const shortDescription = post.shortDescription;
  const maxNumberDisplayCharacters = 650;
  let trimmedShortDescription = "";
  if (shortDescription) {
    trimmedShortDescription = shortDescription.substr(
      0,
      maxNumberDisplayCharacters
    );
    trimmedShortDescription = trimmedShortDescription.substr(
      0,
      Math.min(
        trimmedShortDescription.length,
        trimmedShortDescription.lastIndexOf(" ")
      )
    );
  }
  // Add ellipses to short description (if it exists)
  const desc = trimmedShortDescription ? `${trimmedShortDescription}...` : "";
  // only show the first 2 tags
  const tags = post.tags && post.tags.slice(0, 2);
  // calculate remaining tags to show accurate "+ more"
  const tagCount = post.tags && post.tags.length - tags.length;

  return (
    <div className={classNames(classes.root, className)}>
      <a href={`/blog/post/${post.id}`}>
        <LazyLoad>
          <img
            src={
              `${post.image}` ||
              "https://storage.googleapis.com/branding/elevate-placeholder.svg"
            }
            alt={post.title}
            className={classes.postImage}
          />
        </LazyLoad>
      </a>
      <div>
        <div className={classes.postDate}>
          Posted on {post.createdAtFormatted}
        </div>
        <a className={classes.link} href={`/blog/post/${post.id}`}>
          <div className={classes.postTitle}>{post.title}</div>
        </a>
        {post.listing && renderBlogDisclaimer(post.listing)}
        <div className={classes.postDesc}>
          <div dangerouslySetInnerHTML={{ __html: desc }} />
        </div>
        <Fragment>
          {tags &&
            tags.map((tag, i) => {
              // Remove `null` tags... 🙃
              if (!tag) {
                return null;
              }
              return (
                <div key={i} className={classes.postTagsContainer}>
                  <a
                    className={classes.postTagLink}
                    href={`/blog/category/${encodeURIComponent(tag)}`}
                  >
                    <div className={classes.postTags}>{startCase(tag)}</div>
                  </a>
                </div>
              );
            })}
        </Fragment>
        <Fragment>
          {tagCount > 0 && (
            <a className={classes.link} href={`/blog/post/${post.id}`}>
              <span className={classes.postPlusMore}>+ {tagCount} more</span>
            </a>
          )}
        </Fragment>
        <a className={classes.link} href={`/blog/post/${post.id}`}>
          <div className={classes.postReadMore}>
            Read More{" "}
            <svg
              xmlns="http://www.w3.org/2000/svg"
              width="14"
              height="14"
              viewBox="0 0 24 24"
              style={{ marginLeft: "6px" }}
            >
              <path d="M12 4l-1.41 1.41L16.17 11H4v2h12.17l-5.58 5.59L12 20l8-8z" />
            </svg>
          </div>
        </a>
      </div>
    </div>
  );
};

export default withStyles((theme) => ({
  root: {
    display: "flex",
    flexDirection: "column",
    width: "100%",
    maxWidth: "380px",
    transition: "all 200ms ease-in-out",
    overflow: "hidden",
    userSelect: "none",
  },
  link: {
    color: "inherit !important", // remove default `a` tag styling, along with :hover/:active/:etc.
    textDecoration: "inherit !important", // remove default `a` tag styling, along with :hover/:active/:etc.
  },
  postImage: {
    marginBottom: theme.spacing.unit * 0.5,
    width: "100%",
    height: "220px",
    backgroundSize: "cover",
    objectFit: "contain", // Mobile
    backgroundPosition: "center center",
  },
  postDate: {
    marginBottom: theme.spacing.unit * 0.5,
    fontSize: "12px",
    color: theme.colors.gray["700"],
  },
  postTitle: {
    fontSize: "18px",
    lineHeight: 1.2,
    fontWeight: "600",
    color: theme.colors.gray["900"],
    marginBottom: theme.spacing.unit * 0.25,
  },
  postDesc: {
    display: "flex",
    fontSize: "14px",
    lineHeight: "21px",
    color: theme.colors.gray["700"],
  },
  postTagsContainer: {
    display: "flex",
    flexWrap: "wrap",
    alignItems: "center",
    margin: "8px 0px",
  },
  postTags: {
    marginRight: theme.spacing.unit * 0.5,
    marginBottom: theme.spacing.unit * 0.25,
    fontSize: "13px",
    fontWeight: "600",
    color: theme.colors.gray["600"],
    letterSpacing: ".5px",
    padding: "10px 18px",
    borderRadius: "16px",
    backgroundColor: theme.colors.gray["100"],
    "&:hover": {
      color: theme.colors.gray["700"],
      textDecoration: "none !important",
    },
  },
  postTagLink: {
    "&:hover": {
      textDecoration: "none !important",
    },
  },
  postPlusMore: {
    marginRight: theme.spacing.unit * 0.5,
    marginLeft: theme.spacing.unit * 0.5,
    paddingTop: theme.spacing.unit * 0.5,
    fontSize: "12px",
    fontWeight: "700",
    color: theme.colors.gray["700"],
    letterSpacing: ".5px",
    paddingBottom: theme.spacing.unit * 0.5,
    display: "block",
  },
  postReadMore: {
    textTransform: "uppercase",
    letterSpacing: ".5px",
    color: theme.colors.gray["800"],
    background: theme.colors.gray["200"],
    fontSize: "14px",
    display: "flex",
    alignItems: "center",
    justifyContent: "center",
    padding: theme.spacing.unit,
    fontWeight: "600",
    borderRadius: theme.globalBorderRadius,
    marginTop: theme.spacing.unit * 0.75,

    // large buttons when layout goes to single column
    [theme.breakpoints(600)]: {
      fontSize: "14px",
      color: theme.colors.gray["900"],
      justifyContent: "flex-start",
      background: "none",
      fontWeight: "500",
      paddingLeft: "0px",
    },
  },
}))(BlogPostCard);
